import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  inputClass,
} from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { systemParametersApi } from '../../lib/resources';
import type { ParameterType, SystemParameter } from '../../types';

const TYPE_LABELS: Record<ParameterType, string> = {
  TEXT: 'Texto',
  NUMBER: 'Número',
  BOOLEAN: 'Sim/Não',
  DATE: 'Data',
  JSON: 'JSON',
};

const display = (parameter: SystemParameter) =>
  parameter.type === 'BOOLEAN'
    ? parameter.value === 'true'
      ? 'Sim'
      : 'Não'
    : parameter.type === 'DATE'
      ? parameter.value.split('-').reverse().join('/')
      : parameter.value;

/** Configurações › Parâmetros do sistema (Fase 6 / N1). */
export function SystemParametersSection() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SystemParameter | 'new' | null>(null);
  const [removing, setRemoving] = useState<SystemParameter | null>(null);
  const list = useQuery({ queryKey: ['system-parameters'], queryFn: systemParametersApi.list });
  const remove = useMutation({
    mutationFn: (id: string) => systemParametersApi.remove(id),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ['system-parameters'] });
    },
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Parâmetros do sistema</h2>
          <p className="text-xs text-slate-500">
            Valores globais da empresa. Nas automações:{' '}
            <code className="rounded bg-slate-100 px-1">{'{rótulo|[param]nome}'}</code>
          </p>
        </div>
        {isAdmin && (
          <Button variant="primary" onClick={() => setEditing('new')}>
            <span className="whitespace-nowrap">+ Novo parâmetro</span>
          </Button>
        )}
      </header>
      {list.isLoading ? (
        <Loading />
      ) : list.isError ? (
        <div className="p-5">
          <ErrorNote>{errorMessage(list.error)}</ErrorNote>
        </div>
      ) : !list.data?.data.length ? (
        <div className="p-5">
          <EmptyState
            title="Nenhum parâmetro"
            hint="Ex.: desconto_padrao, link_agenda, telefone_suporte."
          />
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Nome</th>
              <th className="px-4 py-2 font-medium">Tipo</th>
              <th className="px-4 py-2 font-medium">Valor</th>
              <th className="w-28 px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.data.data.map((parameter) => (
              <tr key={parameter.id}>
                <td className="px-4 py-2.5">
                  <code className="font-medium text-slate-800">{parameter.name}</code>
                  {parameter.description && (
                    <p className="text-xs text-slate-400">{parameter.description}</p>
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{TYPE_LABELS[parameter.type]}</td>
                <td className="max-w-xs truncate px-4 py-2.5 text-slate-600">
                  {display(parameter)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {isAdmin && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" onClick={() => setEditing(parameter)}>
                        Editar
                      </Button>
                      <Button variant="ghost" onClick={() => setRemoving(parameter)}>
                        Excluir
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <ParameterModal
          parameter={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['system-parameters'] });
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Excluir parâmetro"
          message={
            <>
              <code>{removing.name}</code> vai para a Lixeira. Automações que usam{' '}
              <code>[param]{removing.name}</code> passam a receber vazio.
            </>
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.isError ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function ParameterModal({
  parameter,
  onClose,
  onSaved,
}: {
  parameter: SystemParameter | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(parameter?.name ?? '');
  const [type, setType] = useState<ParameterType>(parameter?.type ?? 'TEXT');
  const [value, setValue] = useState(parameter?.value ?? '');
  const [description, setDescription] = useState(parameter?.description ?? '');
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        type,
        value,
        description: description.trim() || undefined,
      };
      return parameter
        ? systemParametersApi.update(parameter.id, {
            ...payload,
            description: description.trim() || null,
          })
        : systemParametersApi.create(payload);
    },
    onSuccess: onSaved,
  });

  const valueInput = () => {
    switch (type) {
      case 'BOOLEAN':
        return (
          <select
            className={inputClass}
            value={value || 'true'}
            onChange={(event) => setValue(event.target.value)}
          >
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        );
      case 'DATE':
        return (
          <input
            type="date"
            className={inputClass}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        );
      case 'NUMBER':
        return (
          <input
            inputMode="decimal"
            className={inputClass}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        );
      case 'JSON':
        return (
          <textarea
            className={`${inputClass} font-mono text-xs`}
            rows={5}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        );
      default:
        return (
          <textarea
            className={inputClass}
            rows={3}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        );
    }
  };

  return (
    <Modal title={parameter ? 'Editar parâmetro' : 'Novo parâmetro'} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <Field label="Nome" hint="Letras, números e _ — é como a automação chama: [param]nome">
          <input
            autoFocus
            className={`${inputClass} font-mono`}
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value.replace(/\s/g, '_'))}
          />
        </Field>
        <Field label="Tipo">
          <select
            className={inputClass}
            value={type}
            onChange={(event) => {
              const next = event.target.value as ParameterType;
              setType(next);
              if (next === 'BOOLEAN') setValue('true');
            }}
          >
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Valor">{valueInput()}</Field>
        <Field label="Descrição">
          <input
            className={inputClass}
            value={description}
            maxLength={500}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {save.isError && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
